import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import { DataTable, FilterBar, ConfirmDialog, UserCreateModal } from '../shared';
import { listUsers, updateUserStatus, updateUser } from '../../services/userManagementService';
import PatientAppointmentsModal from './PatientAppointmentsModal';
import { useAuth } from '../../contexts/AuthContext';
import { tokens } from '../../theme/tokens';
import { getUserStatus, getStatusColor } from '../../utils/statusUtils';
import toast from 'react-hot-toast';

/**
 * PatientManagement Component
 * 
 * Comprehensive patient management interface for clinic admins.
 * Features: list, search, filter, view, edit, activate/deactivate patients.
 * Multi-tenant with strict tenantId enforcement.
 */
const PatientManagement = () => {
  const { profile } = useAuth();
  
  // Table state
  const [patients, setPatients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [appointmentsModalOpen, setAppointmentsModalOpen] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  // Fetch patients
  const fetchPatients = useCallback(async () => {
    if (!profile?.tenant_id) {
      setError('No tenant context available');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Convert 0-indexed page (for Material-UI) to 1-indexed (for backend API)
      const apiPage = page + 1;
      
      const result = await listUsers({
        tenantId: profile.tenant_id,
        role: 'patient',
        q: searchTerm,
        status: statusFilter,
        page: apiPage,
        pageSize,
        sortBy,
        sortOrder,
      });

      if (result.error) {
        throw result.error;
      }

      setPatients(result.data.rows);
      setTotalCount(result.data.total);
    } catch (err) {
      console.error('Error fetching patients:', err);
      setError('Failed to load patients. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [profile?.tenant_id, searchTerm, statusFilter, page, pageSize, sortBy, sortOrder]);

  // Load patients on mount and when dependencies change
  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Table columns
  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'N/A',
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
    },
    {
      key: 'phone',
      header: 'Phone',
      width: 150,
      render: (row) => row.phone || 'N/A',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      width: 120,
      render: (row) => {
        const displayStatus = getUserStatus(row.status);
        return (
          <Chip
            label={displayStatus}
            size="small"
            color={getStatusColor(displayStatus)}
          />
        );
      },
    },
    {
      key: 'created_at',
      header: 'Registered',
      sortable: true,
      width: 150,
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
    },
  ];

  // Action handlers
  const handleView = (patient) => {
    setSelectedPatient(patient);
    setViewDialogOpen(true);
  };

  const handleViewAppointments = (patient) => {
    setSelectedPatient(patient);
    setAppointmentsModalOpen(true);
  };

  const handleEdit = (patient) => {
    setSelectedPatient(patient);
    setEditForm({
      first_name: patient.first_name || '',
      last_name: patient.last_name || '',
      email: patient.email || '',
      phone: patient.phone || '',
    });
    setEditDialogOpen(true);
  };

  const handleDeactivate = (patient) => {
    setSelectedPatient(patient);
    setConfirmAction('deactivate');
    setConfirmDialogOpen(true);
  };

  const handleActivate = (patient) => {
    setSelectedPatient(patient);
    setConfirmAction('activate');
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedPatient || !profile?.tenant_id) return;

    const newStatus = confirmAction === 'activate' ? 'active' : 'inactive';
    
    const result = await updateUserStatus(
      selectedPatient.id,
      profile.tenant_id,
      newStatus
    );

    if (result.error) {
      toast.error(`Failed to ${confirmAction} patient`);
    } else {
      await fetchPatients();
    }

    setConfirmDialogOpen(false);
    setSelectedPatient(null);
    setConfirmAction(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedPatient || !profile?.tenant_id) return;

    setLoading(true);
    try {
      const result = await updateUser(
        selectedPatient.id,
        profile.tenant_id,
        editForm
      );

      if (result.error) {
        throw result.error;
      }

      await fetchPatients();
      setEditDialogOpen(false);
      setSelectedPatient(null);
    } catch (err) {
      console.error('Error updating patient:', err);
      toast.error('Failed to update patient');
    } finally {
      setLoading(false);
    }
  };

  // Actions slot for table
  const actionsSlot = (row) => (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
      <IconButton
        size="small"
        onClick={() => handleViewAppointments(row)}
        aria-label={`View appointments for ${row.first_name} ${row.last_name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <EventIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => handleView(row)}
        aria-label={`View ${row.first_name} ${row.last_name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => handleEdit(row)}
        aria-label={`Edit ${row.first_name} ${row.last_name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      {row.status === 'active' ? (
        <IconButton
          size="small"
          onClick={() => handleDeactivate(row)}
          aria-label={`Deactivate ${row.first_name} ${row.last_name}`}
          color="error"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.danger}`,
              outlineOffset: 2,
            },
          }}
        >
          <PersonOffIcon fontSize="small" />
        </IconButton>
      ) : (
        <IconButton
          size="small"
          onClick={() => handleActivate(row)}
          aria-label={`Activate ${row.first_name} ${row.last_name}`}
          color="success"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.success}`,
              outlineOffset: 2,
            },
          }}
        >
          <PersonIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  // Clear filters handler
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPage(0);
  };

  return (
    <Box sx={{ py: 0, px: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25, fontSize: '1.125rem' }}>
            Patient Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            Manage and monitor patient accounts
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.primary}`,
              outlineOffset: 2,
            },
          }}
        >
          Add Patient
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Bar */}
      <FilterBar
        value={searchTerm}
        onChange={setSearchTerm}
        onClear={handleClearFilters}
        placeholder="Search by name, email, or phone..."
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            label="Status"
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>
      </FilterBar>

      {/* Stats */}
      <Box sx={{ mb: 1, mt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {totalCount} total patient{totalCount !== 1 ? 's' : ''}
          {statusFilter !== 'all' && ` (filtered by ${statusFilter})`}
        </Typography>
      </Box>

      {/* Data Table */}
      <DataTable
        columns={columns}
        rows={patients}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        sortBy={sortBy}
        sortDir={sortOrder}
        loading={loading}
        error={error}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        onSortChange={(key, dir) => {
          setSortBy(key);
          setSortOrder(dir);
        }}
        getRowId={(row) => row.id}
        actionsSlot={actionsSlot}
        emptyMessage="No patients found. Try adjusting your filters or add a new patient."
        ariaLabel="patients table"
      />

      {/* Appointments Modal */}
      {selectedPatient && (
        <PatientAppointmentsModal
          open={appointmentsModalOpen}
          onClose={() => setAppointmentsModalOpen(false)}
          patient={selectedPatient}
        />
      )}

      {/* Create User Modal */}
      <UserCreateModal
        open={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onUserCreated={fetchPatients}
        role="patient"
      />

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Patient Details</DialogTitle>
        <DialogContent>
          {selectedPatient && (
            <Box sx={{ pt: tokens.space.x2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedPatient.first_name} {selectedPatient.last_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip label={selectedPatient.status} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedPatient.email}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedPatient.phone || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Registered</Typography>
                  <Typography variant="body1">
                    {selectedPatient.created_at ? new Date(selectedPatient.created_at).toLocaleString() : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Patient</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: tokens.space.x2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title={confirmAction === 'activate' ? 'Activate Patient' : 'Deactivate Patient'}
        message={
          selectedPatient
            ? `Are you sure you want to ${confirmAction} ${selectedPatient.first_name} ${selectedPatient.last_name}?`
            : ''
        }
        onConfirm={handleConfirmAction}
        onCancel={() => {
          setConfirmDialogOpen(false);
          setSelectedPatient(null);
          setConfirmAction(null);
        }}
        confirmText={confirmAction === 'activate' ? 'Activate' : 'Deactivate'}
        severity={confirmAction === 'deactivate' ? 'warning' : 'info'}
      />
    </Box>
  );
};

export default PatientManagement;
